import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Shield, GoogleLogo, Lock, At, IdentificationCard } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const Login = () => {
  const { user, loginWithPassword, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("error") === "oauth") {
      toast.error("Google sign-in failed. Try again.");
    }
  }, [location.search]);

  const handleGoogle = () => {
    // Derive redirect dynamically from current origin
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") {
        await loginWithPassword(email, password);
        toast.success("Welcome back, dispatcher.");
      } else {
        await register(email, name, password);
        toast.success("Dispatcher account created.");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[#0A0A0A] text-white">
      {/* Left brand panel */}
      <div className="lg:col-span-3 relative hidden lg:block overflow-hidden border-r border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/18441167/pexels-photo-18441167.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute inset-0 scanline" />

        <div className="relative h-full p-12 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center bg-[#007AFF]">
              <Shield size={22} weight="fill" />
            </div>
            <div>
              <div className="font-display font-black text-xl tracking-tight">SANDY</div>
              <div className="label-mono">Automated Security Dispatch</div>
            </div>
          </div>

          <div className="space-y-6 max-w-xl">
            <div className="label-mono">// SECURITY OPS · SMS DISPATCH</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95]">
              Every guard.<br />
              Every shift.<br />
              <span className="text-[#2BD4D9]">A text away.</span>
            </h1>
            <p className="text-zinc-400 max-w-md leading-relaxed">
              Sandy texts your guards before each shift, listens for their reply,
              and escalates the moment a post goes silent.
            </p>
            <div className="grid grid-cols-3 gap-px bg-white/10 max-w-md">
              {[
                ["AUTO PING", "T-10m"],
                ["ESCALATION", "T+5m"],
                ["NO-SHOW", "T+15m"],
              ].map(([k, v]) => (
                <div key={k} className="bg-[#0A0A0A] p-4">
                  <div className="label-mono">{k}</div>
                  <div className="font-mono text-2xl text-white mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="label-mono opacity-60">CHANNEL ENCRYPTED · UTC SYNCED</div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md panel-in">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-9 h-9 grid place-items-center bg-[#007AFF]">
              <Shield size={20} weight="fill" />
            </div>
            <div className="font-display font-black text-lg">SANDY</div>
          </div>

          <div className="label-mono mb-3">// {mode === "login" ? "DISPATCHER LOGIN" : "NEW DISPATCHER"}</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8">
            {mode === "login" ? "Sign in to command." : "Open a new channel."}
          </h2>

          <button
            data-testid="google-login-btn"
            onClick={handleGoogle}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-white/15 hover:border-white/30 hover:bg-white/5 transition-colors text-sm font-medium"
          >
            <GoogleLogo size={18} weight="bold" />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="label-mono">or with credentials</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <IdentificationCard className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <input
                  data-testid="name-input"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full Name"
                  className="w-full bg-[#121212] border border-white/10 focus:border-[#007AFF] outline-none pl-10 pr-3 py-3 text-sm"
                />
              </div>
            )}
            <div className="relative">
              <At className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <input
                data-testid="email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full bg-[#121212] border border-white/10 focus:border-[#007AFF] outline-none pl-10 pr-3 py-3 text-sm"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <input
                data-testid="password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full bg-[#121212] border border-white/10 focus:border-[#007AFF] outline-none pl-10 pr-3 py-3 text-sm"
              />
            </div>

            <button
              data-testid="submit-auth-btn"
              type="submit"
              disabled={submitting}
              className="w-full mt-2 bg-[#007AFF] hover:bg-[#0066d6] disabled:opacity-50 transition-colors text-white px-4 py-3 text-sm font-semibold tracking-wide uppercase"
            >
              {submitting ? "..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center text-xs text-zinc-500">
            {mode === "login" ? (
              <>
                No account yet?{" "}
                <button
                  data-testid="switch-to-register"
                  onClick={() => setMode("register")}
                  className="text-white hover:text-[#2BD4D9] underline underline-offset-4"
                >
                  Create dispatcher account
                </button>
              </>
            ) : (
              <>
                Already a dispatcher?{" "}
                <button
                  data-testid="switch-to-login"
                  onClick={() => setMode("login")}
                  className="text-white hover:text-[#2BD4D9] underline underline-offset-4"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
